<script>
import axios from '@/utils/axios';
import { Offcanvas } from 'bootstrap';

const SESSIONS = [
  { value: 'full_day', label: 'Full Day' },
  { value: 'half_day_morning', label: 'Half Day (Morning)' },
  { value: 'half_day_afternoon', label: 'Half Day (Afternoon)' },
  { value: 'short_day_morning', label: 'Short Day (Morning)' },
  { value: 'short_day_afternoon', label: 'Short Day (Afternoon)' },
];

export default {
  emits: ['updated'],
  data() {
    return {
      saving: false,
      employees: [],
      leaveTypes: [],
      sessions: SESSIONS,
      form: { employee_id: '', leave_type_id: '', from_date: '', to_date: '', session: 'full_day', reason: '' },
      errors: {},
      needsOverride: false,
      overrideMsg: '',
      overrideRemaining: null,
      overrideReason: '',
      oc: null,
    };
  },

  computed: {
  employeeOptions() {
    return this.employees.map(e => ({ id: e.id, label: this.empLabel(e) }));
  },
  leaveTypeOptions() {
    return this.leaveTypes.map(t => ({ id: t.id, label: t.code }));
  },
},

  mounted() {
    this.fetchEmployees();
    this.fetchLeaveTypes();
  },
  methods: {
    async fetchEmployees() {
      try { const res = await axios.get('/hrms/employees/dropdown'); this.employees = res.data.data || []; }
      catch (e) { console.log(e.message); }
    },
    async fetchLeaveTypes() {
      try { const res = await axios.get('/hrms/leave-types/dropdown'); this.leaveTypes = res.data.data || []; }
      catch (e) { console.log(e.message); }
    },
    empLabel(e) { return e.full_name || e.name || [e.first_name, e.last_name].filter(Boolean).join(' ') || `#${e.id}`; },
    open() {
      this.reset();
      this.oc = Offcanvas.getOrCreateInstance(this.$refs.oc);
      this.oc.show();
    },
    close() { this.oc?.hide(); },
    reset() {
      this.form = { employee_id: '', leave_type_id: '', from_date: '', to_date: '', session: 'full_day', reason: '' };
      this.errors = {};
      this.needsOverride = false;
      this.overrideMsg = '';
      this.overrideRemaining = null;
      this.overrideReason = '';
    },
    errText(f) { const v = this.errors[f]; if (!v) return ''; return Array.isArray(v) ? v[0] : v; },
    validate() {
      const e = {};
      if (!this.form.employee_id) e.employee_id = 'Employee is required';
      if (!this.form.leave_type_id) e.leave_type_id = 'Leave type is required';
      if (!this.form.from_date) e.from_date = 'From date is required';
      if (!this.form.to_date) e.to_date = 'To date is required';
      if (this.form.from_date && this.form.to_date && this.form.to_date < this.form.from_date) e.to_date = 'To date must be after from date';
      if (!this.form.session) e.session = 'Session is required';
      if (this.needsOverride && !this.overrideReason.trim()) e.override_reason = 'Override reason is required';
      this.errors = e;
      return !Object.keys(e).length;
    },
    async submit() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        const payload = {
          employee_id: this.form.employee_id,
          leave_type_id: this.form.leave_type_id,
          from_date: this.form.from_date,
          to_date: this.form.to_date,
          session: this.form.session,
          reason: this.form.reason || null,
        };
        if (this.needsOverride) {
          payload.override = true;
          payload.override_reason = this.overrideReason.trim();
        }
        const res = await axios.post('/hrms/leaves/mark-leave', payload);
        // requires_override data ya root me ho sakta hai — dono check
        const body = (res.data && typeof res.data.data === 'object' && res.data.data) ? res.data.data : res.data;
        if (body && body.requires_override && !this.needsOverride) {
          this.needsOverride = true;
          this.overrideMsg = body.message || res.data.message || 'Insufficient balance.';
          this.overrideRemaining = body.remaining;
          this.saving = false;
          return; // admin confirm karega phir override
        }
        this.$swalToast(res.data.message || 'Leave marked.');
        this.$emit('updated');
        this.close();
      } catch (error) {
        if (error.type === 'validation') this.errors = error.errors;
        else this.$swalToast(error.message, 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div ref="oc" class="offcanvas offcanvas-end lv-oc" tabindex="-1" id="hrm_leave_mark_modal">
    <div class="offcanvas-header">
      <div class="d-flex align-items-center gap-2">
        <span class="lv-oc-ico"><i class="ti ti-calendar-bolt"></i></span>
        <h5 class="offcanvas-title mb-0">Mark Leave</h5>
      </div>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas"></button>
    </div>

    <div class="offcanvas-body">
      <div class="mb-3">
        <label class="lv-lbl">Employee <span class="text-danger">*</span></label>
        <vue-select v-model="form.employee_id" :options="employeeOptions" label="label" :reduce="e => e.id"
          placeholder="Select employee" :clearable="false" :disabled="needsOverride"
          :class="{ 'lv-vsel-invalid': errText('employee_id') }" />
        <div v-if="errText('employee_id')" class="lv-err">{{ errText('employee_id') }}</div>
      </div>

      <div class="mb-3">
        <label class="lv-lbl">Leave Type <span class="text-danger">*</span></label>
        <vue-select v-model="form.leave_type_id" :options="leaveTypeOptions" label="label" :reduce="t => t.id"
          placeholder="Select leave type" :clearable="false" :disabled="needsOverride"
          :class="{ 'lv-vsel-invalid': errText('leave_type_id') }" />
        <div v-if="errText('leave_type_id')" class="lv-err">{{ errText('leave_type_id') }}</div>
      </div>

      <div class="row g-2 mb-3 lv-date-row">
        <div class="col-6">
          <label class="lv-lbl">From <span class="text-danger">*</span></label>
          <input type="date" class="form-control" v-model="form.from_date" :class="{ 'is-invalid': errText('from_date') }" :disabled="needsOverride" />
          <div v-if="errText('from_date')" class="lv-err">{{ errText('from_date') }}</div>
        </div>
        <div class="col-6">
          <label class="lv-lbl">To <span class="text-danger">*</span></label>
          <input type="date" class="form-control" :min="form.from_date" v-model="form.to_date" :class="{ 'is-invalid': errText('to_date') }" :disabled="needsOverride" />
          <div v-if="errText('to_date')" class="lv-err">{{ errText('to_date') }}</div>
        </div>
      </div>

      <div class="mb-3">
        <label class="lv-lbl">Session <span class="text-danger">*</span></label>
        <select class="form-select" v-model="form.session" :disabled="needsOverride">
          <option v-for="s in sessions" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="lv-lbl">Reason</label>
        <textarea class="form-control" rows="2" maxlength="500" v-model="form.reason" placeholder="Optional — reason" :disabled="needsOverride"></textarea>
      </div>

      <!-- Override block -->
      <div v-if="needsOverride" class="lv-override">
        <div class="lv-override-head"><i class="ti ti-alert-triangle me-1"></i>Insufficient balance</div>
        <p class="lv-override-msg">
          {{ overrideMsg }}
          <span v-if="overrideRemaining !== null">(Remaining: {{ overrideRemaining }})</span>
        </p>
        <label class="lv-lbl">Override Reason <span class="text-danger">*</span></label>
        <textarea class="form-control" rows="2" maxlength="500" v-model="overrideReason" :class="{ 'is-invalid': errText('override_reason') }" placeholder="Why override the balance?"></textarea>
        <div v-if="errText('override_reason')" class="lv-err">{{ errText('override_reason') }}</div>
      </div>
    </div>

    <div class="lv-oc-footer">
      <button type="button" class="btn btn-light" data-bs-dismiss="offcanvas">Cancel</button>
      <button type="button" class="btn" :class="needsOverride ? 'btn-danger' : 'btn-primary'" :disabled="saving" @click="submit">
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        {{ saving ? 'Saving…' : (needsOverride ? 'Override & Mark' : 'Mark Leave') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.lv-oc { width: 520px; max-width: 92vw; }
.offcanvas-header { border-bottom: 1px solid #f1f5f9; padding: 18px 22px; }
.offcanvas-title { font-size: 16px; font-weight: 700; color: #0f172a; }
.lv-oc-ico { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff;
  display: inline-flex; align-items: center; justify-content: center; font-size: 18px; }
.offcanvas-body { padding: 22px; }
.lv-lbl { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 5px; display: block; }
.form-control, .form-select { border-radius: 10px; border-color: #e2e8f0; font-size: 13.5px; padding: 9px 12px; }
.form-control:focus, .form-select:focus { border-color: #a5b4fc; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
.form-control:disabled, .form-select:disabled { background:#f8fafc; opacity:.7; }
.lv-err { font-size: 11px; color: #dc2626; margin-top: 4px; }

/* From/To date fields — spacing fix */
.lv-date-row {
  --bs-gutter-x: 14px;
}
.lv-date-row input[type="date"] {
  padding: 9px 8px;
  font-size: 12.5px;
  min-width: 0;
}
@media (max-width: 360px) {
  .lv-date-row .col-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }
  .lv-date-row .col-6:first-child {
    margin-bottom: 10px;
  }
}

.lv-override { background:#fff7ed; border:1px solid #fed7aa; border-radius:12px; padding:14px; margin-top:4px; }
.lv-override-head { font-size:12.5px; font-weight:700; color:#c2410c; margin-bottom:6px; }
.lv-override-msg { font-size:12px; color:#9a3412; margin-bottom:10px; }
.lv-oc-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 22px; border-top: 1px solid #f1f5f9; margin-top: auto; }
.lv-oc-footer .btn { border-radius: 10px; font-size: 13px; font-weight: 600; padding: 9px 18px; }
.btn-primary { background: linear-gradient(135deg, #4f46e5, #7c3aed); border: none; }
.btn-light { background: #f8fafc; border: 1px solid #e2e8f0; color: #475569; }

/* vue-select styling to match form-control look */
:deep(.vs__dropdown-toggle) {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  min-height: 38px;
  background: #fff;
  padding: 0 4px;
}
:deep(.vs__dropdown-toggle:focus-within) {
  border-color: #a5b4fc;
  box-shadow: 0 0 0 3px rgba(79,70,229,.1);
}
:deep(.vs__search), :deep(.vs__search:focus) { font-size: 13.5px; margin: 0; padding: 6px 4px; }
:deep(.vs__selected) { font-size: 13.5px; color: #1e293b; margin: 4px 2px; }
:deep(.vs__dropdown-option) { font-size: 13.5px; }
:deep(.vs__dropdown-menu) { font-size: 13.5px; }
:deep(.vs__clear), :deep(.vs__open-indicator) { fill: #94a3b8; }
:deep(.vs--disabled .vs__dropdown-toggle) { background: #f8fafc; opacity: .7; }

.lv-vsel-invalid :deep(.vs__dropdown-toggle) { border-color: #dc2626; }
</style>